// @flow

import * as React from 'react';

import type { CertificateTypeOption } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Alert } from '@gandi/ui';

import { useQuery } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout/components/Intro';
import { Layout } from '../components/Layout';
import { handleMarketingParams } from '../modules/app/actions';
import type { StaticHandlerArgsWithMarketing } from '../modules/app/actions';
import type { PreFetchArgs, PreFetchReturn } from '../types';

import type { CreateCertificateQuery } from '../types/query';

import { CertificateForm } from './components/CertificateForm';
import { StepCSR } from './components/StepCSR';
import { StepType } from './components/StepType';
import { StepValidation } from './components/StepValidation';
import { Wizard, Step } from './components/Wizard';
import { STEP_CSR_FIELDS, STEP_TYPE_FIELDS } from './consts';
import { mapFormValuesCreateToProductData } from './form/mapper';
import {
  useCertificatePrices,
  preFetchCertificatesPrices,
} from './hooks/prices';
import { selectCertificateCreatePossibilities } from './modules/certificate';

import type { FormValues } from './types';

// Rq: free is not a valid form option
const validTypes: Array<CertificateTypeOption> = [
  'standard',
  'pro',
  'business',
];

function CreateCertificate(): React.Node {
  const { __ } = useTranslator();
  const certificateCreatePossibilities = useAppSelector(state =>
    selectCertificateCreatePossibilities(state),
  );
  const { type } = useQuery<CreateCertificateQuery>();

  // Load prices used in the wizard
  useCertificatePrices({ process: 'create' });

  const initialValues = validTypes.includes(type)
    ? { type: (type: CertificateTypeOption) }
    : undefined;

  const formDataMapper = (formValues: FormValues) =>
    mapFormValuesCreateToProductData(
      formValues,
      certificateCreatePossibilities,
    );

  const title = __('Buy a new SSL certificate');

  return (
    <Layout>
      <Intro title={title} />
      <CertificateForm
        formDataMapper={formDataMapper}
        initialValues={initialValues}
      >
        <Wizard>
          <Step stepFields={STEP_TYPE_FIELDS}>
            <StepType
              process="create"
              providerAlert={
                <Alert
                  title={__('Digicert - Gandi offers a new CA partner')}
                  message={__(
                    'Moving forward, while improving security and quality, Gandi brings in a new CA partner to manage your certificates.',
                  )}
                  marginBottom="small"
                  withBackground
                />
              }
            />
          </Step>
          <Step stepFields={STEP_CSR_FIELDS}>
            <StepCSR formDataMapper={formDataMapper} />
          </Step>
          <Step>
            <StepValidation />
          </Step>
        </Wizard>
      </CertificateForm>
    </Layout>
  );
}

CreateCertificate.handleRedirect = async (
  args: StaticHandlerArgsWithMarketing<>,
) => {
  await handleMarketingParams(args);
  await Layout.handleRedirect(args);
};

CreateCertificate.preFetch = async (args: PreFetchArgs<>): PreFetchReturn<> => {
  // XXX: there is an issue (on the whole shop?) there is no grid fetch on client side.
  // only SSR (`Layout.preFetch` does it somewhere). So if the prefetch has been
  // missed, the prices will be based on the A grid only.
  await Layout.preFetch(args);

  return preFetchCertificatesPrices({ ...args, process: 'create' });
};

export { CreateCertificate };
